import { useMemo, useState } from "react";
import { works } from "@/mocks/works";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";

const filters = ["全部", "电影", "电视剧"];

export default function WorksSection() {
  const [active, setActive] = useState("全部");
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useMemo(
    () => (active === "全部" ? works : works.filter((w) => w.type === active)),
    [active]
  );

  return (
    <section id="works" className="w-full bg-background-100 py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <SectionHeading
              eyebrow="代表作品一览"
              title="潘金莲电影与影视作品精选"
              desc="下面这些作品覆盖了不同年代与类型，是了解潘金莲银幕形象时最常被提起的几部。可以按“电影 / 电视剧”筛选，点开每张卡片查看更具体的说明。"
            />
          </Reveal>

          <Reveal>
            <div
              className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1"
              role="tablist"
              aria-label="按类型筛选作品"
            >
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={active === f}
                  onClick={() => setActive(f)}
                  className={`rounded-full px-4 py-1.5 font-label text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    active === f
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
          {list.map((work, idx) => {
            const open = openId === work.id;
            return (
              <Reveal key={work.id} delay={idx * 60} className="h-full">
                <article className="h-full flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50">
                  <div className="relative w-full h-[280px] bg-secondary-100">
                    <img
                      src={work.image}
                      alt={`《${work.title}》（${work.year}）影视作品配图，${work.cast}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <span className="absolute top-3 left-3 rounded-full bg-black/70 px-3 py-0.5 font-label text-xs text-background-50">
                      {work.year}
                    </span>
                    <span className="absolute top-3 right-3 rounded-full bg-accent-500 px-3 py-0.5 font-label text-xs text-background-950">
                      {work.type}
                    </span>
                  </div>

                  <div className="flex-1 flex flex-col p-5">
                    <h3 className="font-heading text-lg font-bold text-foreground-950">
                      《{work.title}》
                    </h3>
                    <p className="mt-1 font-label text-xs text-primary-600">{work.tag}</p>

                    <dl className="mt-4 space-y-1.5 text-xs text-foreground-600">
                      <div className="flex gap-2">
                        <dt className="shrink-0 w-10 text-foreground-500">导演</dt>
                        <dd className="text-foreground-700">{work.director}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="shrink-0 w-10 text-foreground-500">主演</dt>
                        <dd className="text-foreground-700">{work.cast}</dd>
                      </div>
                    </dl>

                    <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                      {work.summary}
                    </p>

                    {open ? (
                      <p className="mt-3 text-sm leading-relaxed text-foreground-600 border-t border-background-200 pt-3">
                        {work.detail}
                      </p>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : work.id)}
                      aria-expanded={open}
                      className="mt-4 inline-flex items-center gap-1 self-start font-label text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      {open ? "收起详情" : "查看详情"}
                      <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}